import { LocalDate } from '@js-joda/core';
import { useCallback, useMemo } from 'react';
import { useMount } from 'react-use';
import styled from 'styled-components';

import { Country } from '@dreryk/address-common-api';
import { PatientExternalIdentifier } from '@dreryk/egabinet-patient-published-language-api';
import { NationalIdentificationNumberCountry } from '@dreryk/personal-external-identifier-common-api';

import { FormContextValues, useFormController } from '@dreryk/react-common';

import {
   BirthDateTextView,
   createPatientExternalIdentifierTypeVisitor,
   GenderTextView,
   NationalIdentificationNumberCountryField,
   PassportCountryField,
   PassportValidToField,
   PatientExternalIdentifierType,
   PatientExternalIdentifierTypeField,
   PatientExternalIdentifierTypeVisitor,
   patientExternalIdentifierValueDefaultValidationRules,
   PatientExternalIdentifierValueField,
} from '~libs.desktop.shared.fields';

const StyledColumn = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   column-gap: 24px;
`;

StyledColumn.displayName = 'StyledColumn';

const StyledForm = styled.form`
   display: grid;
   grid-template-columns: 1fr 1fr;
   column-gap: 36px;
   row-gap: 24px;
`;

StyledForm.displayName = 'StyledForm';

export type PatientExternalIdentifierTypeStillValidFormBean = {
   patientExternalIdentifierType: PatientExternalIdentifierType;
   patientExternalIdentifierValue: string | null;
   passportCountry: Country | null;
   passportValidTo: LocalDate | null;
   nationalIdentificationNumberCountry: NationalIdentificationNumberCountry | null;
};

export type PatientExternalIdentifierTypeStillValidFormController = FormContextValues<PatientExternalIdentifierTypeStillValidFormBean>;

declare type PatientExternalIdentifierTypeStillValidFormRendererProps = Pick<
   PatientExternalIdentifierTypeStillValidFormController,
   'handleSubmit' | 'formState'
>;

declare type PatientExternalIdentifierTypeStillValidFormRenderer = (
   renderProps: PatientExternalIdentifierTypeStillValidFormRendererProps
) => JSX.Element;

type PatientExternalIdentifierTypeStillValidFormProps = {
   readonly patientExternalIdentifier: PatientExternalIdentifier | undefined;
   readonly children: PatientExternalIdentifierTypeStillValidFormRenderer;
};

// eslint-disable-next-line max-lines-per-function
export const PatientExternalIdentifierTypeStillValidForm = (
   props: PatientExternalIdentifierTypeStillValidFormProps
): JSX.Element => {
   const {
      register,
      handleSubmit,
      formState,
      setValue,
      setFocus,
      watch,
      trigger,
   } = useFormController<PatientExternalIdentifierTypeStillValidFormBean>(
      props.patientExternalIdentifier?.accept<Partial<PatientExternalIdentifierTypeStillValidFormBean>>({
         visitPesel: (pesel) => ({
            patientExternalIdentifierType: 'Pesel',
            patientExternalIdentifierValue: pesel.asString(),
         }),
         visitGuardianPesel: () => ({
            patientExternalIdentifierType: 'Pesel',
            patientExternalIdentifierValue: null,
         }),
         visitPassport: (passport) => ({
            patientExternalIdentifierType: 'Passport',
            patientExternalIdentifierValue: passport.getValue(),
            passportCountry: passport.getCountry(),
            passportValidTo: passport.getValidTo(),
         }),
         visitNationalIdentificationNumber: (nationalIdentificationNumber) => ({
            patientExternalIdentifierType: 'NationalIdentificationNumber',
            patientExternalIdentifierValue: nationalIdentificationNumber.getValue(),
         }),
      }) ?? {
         patientExternalIdentifierType: 'NationalIdentificationNumber',
         patientExternalIdentifierValue: null,
      }
   );

   const { patientExternalIdentifierType, patientExternalIdentifierValue } = watch([
      'patientExternalIdentifierType',
      'patientExternalIdentifierValue',
   ]);

   const patientExternalIdentifierTypeVisitor = useMemo<PatientExternalIdentifierTypeVisitor>(
      () => createPatientExternalIdentifierTypeVisitor(patientExternalIdentifierType),
      [patientExternalIdentifierType]
   );

   const onPatientExternalIdentifierType = useCallback(() => {
      setValue('patientExternalIdentifierValue', null);
      setFocus('patientExternalIdentifierValue');
   }, [setValue, setFocus]);

   const patientExternalIdentifierValueValidationRules = useMemo(
      () => patientExternalIdentifierValueDefaultValidationRules(patientExternalIdentifierTypeVisitor),
      [patientExternalIdentifierTypeVisitor]
   );

   useMount(() =>
      patientExternalIdentifierTypeVisitor<void>({
         visitPesel: () => {
            setFocus('patientExternalIdentifierValue');
         },
         visitPassport: () => {
            trigger('passportValidTo');
            setFocus('passportValidTo');
         },
         visitNationalIdentificationNumber: () => {
            setFocus('patientExternalIdentifierValue');
         },
      })
   );

   return (
      <>
         <StyledForm noValidate autoComplete={'off'}>
            <PatientExternalIdentifierTypeField
               controller={register('patientExternalIdentifierType', {
                  required: true,
               })}
               onSelect={onPatientExternalIdentifierType}
            />
            <PatientExternalIdentifierValueField
               controller={register('patientExternalIdentifierValue', patientExternalIdentifierValueValidationRules)}
               patientExternalIdentifierTypeVisitor={patientExternalIdentifierTypeVisitor}
            />
            {patientExternalIdentifierTypeVisitor({
               visitPesel: () => (
                  <StyledColumn>
                     <BirthDateTextView peselValue={patientExternalIdentifierValue} />
                     <GenderTextView peselValue={patientExternalIdentifierValue} />
                  </StyledColumn>
               ),

               visitPassport: () => (
                  <>
                     <PassportCountryField
                        controller={register('passportCountry', {
                           required: true,
                        })}
                     />
                     <PassportValidToField
                        controller={register('passportValidTo', {
                           required: true,
                        })}
                     />
                  </>
               ),

               visitNationalIdentificationNumber: () => (
                  <NationalIdentificationNumberCountryField
                     controller={register('nationalIdentificationNumberCountry', {
                        required: true,
                     })}
                  />
               ),
            })}
         </StyledForm>

         {props.children({
            handleSubmit: handleSubmit,
            formState: formState,
         })}
      </>
   );
};
